<template>
  <div class="order-list-container">

    <data-table :data-set="orders" :page-length="50" :columns="[
      {name: 'Order', prop: 'id', type: 'number'},
      {name: 'Customer', prop: ['first_name', 'last_name', 'email'], type: 'text'},
      {name: 'Account', prop: 'account_number', type: 'number'},
      {name: 'Transaction', prop: ['payment_method', 'discount_code'], type: 'text'},
      {name: 'Total', prop: 'total', type: 'number'},
      {name: 'Items', prop: 'items', display: false, nested: true, subprop: 'product_sku', type: 'string'},
      {name: 'Submitted', prop: 'created_at', type: 'date', searchable: false, filter: ['moment', ['from', 'now']]},
    ]">
      <template slot="column_Order" slot-scope="props">
        <router-link :to="{name: 'order', params:{id: props.row.id}}">
          #{{ props.row.id }}
          <br />
          View Order
        </router-link>
      </template>
      <template slot="column_Customer" slot-scope="props">
        <router-link v-if="props.row.user_id" :to="{name:'user', params:{id:props.row.user_id}}">
          {{ props.row.first_name }} {{ props.row.last_name }}
          <i class="fa fa-user" v-if="props.row.user_id"></i>
        </router-link>
        <span v-else>
          {{ props.row.first_name }} {{ props.row.last_name }}
        </span>
        <br />
        {{ props.row.line1 }}
        <br />
        Zip: <strong>{{ props.row.zip }}</strong>
      </template>
      <template slot="column_Account" slot-scope="props">
        <div @click="editAcctNum(props.row)"
             style="min-width: 110px;"
             class="edit-account-number"
             v-if="acct.order_id !== props.row.id">
          <div v-if="props.row.account_number"><i class="fa fa-edit"></i> &nbsp; {{ props.row.account_number }}</div>
          <div v-else><i class="fa fa-edit"></i> &nbsp;&nbsp;<em>- none -</em></div>
        </div>
        <div v-if="acct_edit && acct.order_id === props.row.id">
          <input type="text" class="input" name="acct_edit" v-model="acct.current_acct_num" style="max-width: 90px;"
                 maxlength="8">
          <div>
            <button type="button" class="button is-small" @click="cancelAcctEdit()"
            >CANCEL</button>
            <button type="button" class="button is-small is-primary"
                    v-if="acct.current_acct_num !== acct.start_acct_num"
                    @click="updateAccountNumber()"
            >SAVE</button>
          </div>
        </div>
      </template>
      <template slot="column_Transaction" slot-scope="props">
        {{ props.row.payment_method }}
        <span v-if="props.row.discount_code !== null">
          <br />
          <strong>{{ props.row.discount_code }}</strong> Discount
        </span>
        <hr class="table-hr" />
        {{ reduceOrderItems(props.row.items) }}
      </template>
      <template slot="column_Total" slot-scope="props">
        <div class="has-text-right">
          {{ (props.row.discounted_total !== null ? props.row.discounted_total : props.row.total) | currency }}
        </div>
      </template>
      <template slot="controls" slot-scope="props">
        <td>
          <div class="level is-mobile">
            <div class="level-left">
              <div class="level-item">
                <span @click="printOrder(props.row)"
                      class="clickable icon"
                      :title="props.row.printed ? 'Reprint Order' : 'Print Order'">
                    <i class="fa fa-print"></i>
                </span>
              </div>
              <div class="level-item" v-if="props.row.printed === true && isSuperUser">
                <span v-if="props.row.archived === true" @click="unarchiveOrder(props.row)"
                      class="clickable fa-stack" title="Unarchive Order">
                    <i class="fa fa-archive fa-stack-1x"></i>
                    <i class="fa fa-ban fa-stack-2x text-danger"></i>
                </span>
                <span v-else @click="archiveOrder(props.row)"
                      class="clickable icon" title="Archive Order">
                    <i class="fa fa-archive"></i>
                </span>
              </div>
              <div class="level-item" v-if="isSuperUser">
                <span v-if="props.row.voided !== true" @click="voidOrder(props.row)"
                      class="clickable icon" title="Void Order">
                    <i class="fa fa-ban text-danger"></i>
                </span>
                <span v-else @click="unvoidOrder(props.row)"
                      class="clickable icon" title="Reinstate Order">
                    <i class="fa fa-undo text-danger"></i>
                </span>
              </div>
            </div>
          </div>
        </td>
      </template>
    </data-table>

    <div class="void-order-reason-container" v-if="voidingOrder !== null">
      <div class="void-order-reason-dialog">
        <h1 class="title is-2">Voiding Order #{{ voidingOrder }}</h1>
        <form-group type="text"
                    label="Why are you voiding this order?"
                    id="void_reason"
                    placeholder="Don't like this customer"
                    :error="voidForm.errors.get('voidReason')"
                    @event="voidForm.listen(voidForm, $event)"
                    v-model="voidForm.voidReason">
        </form-group>
        <button class="button is-dark" @click="voidingOrder = null">Cancel</button>
        <button class="is-pulled-right button is-primary" @click="voidOrder()">Okay, void it.</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  MARK_PRINTED_ORDERS,
  MARK_UNPRINTED_ORDERS,
  ARCHIVE_ORDER,
  UNARCHIVE_ORDER,
  VOID_ORDER,
  UNVOID_ORDER,
} from "../store/mutation-types"
import { mapGetters } from "vuex"
import Form from "../helpers/Form"

export default {
  props: ['orders'],
  data() {
    return {
      voidingOrder: null,
      updating: false,
      voidForm: new Form(this, { voidReason: null }),
      acct_edit: false,
      acct: {
        order_id: null,
        start_acct_num: null,
        current_acct_num: null,
      },
    }
  },
  computed: mapGetters(['isSuperUser']),
  methods: {
    editAcctNum(order) {
      this.acct_edit             = true;
      this.acct.row              = order;
      this.acct.order_id         = order.id;
      this.acct.start_acct_num   = order.account_number;
      this.acct.current_acct_num = JSON.parse(JSON.stringify(order.account_number));
      setTimeout(() => {
        window.document.getElementsByName('acct_edit')[0].focus();
      }, 0)
    },
    cancelAcctEdit() {
      this.acct_edit             = false;
      this.acct.order_id         = null;
      this.acct.start_acct_num   = null;
      this.acct.current_acct_num = null;
    },
    updateAccountNumber() {
      this.$store.dispatch('UPDATE_ACCOUNT_NUMBER', {
        order_id: this.acct.order_id,
        account_number: this.acct.current_acct_num,
      })
          .then(data => {
            this.acct.row.account_number = data.order.account_number
            this.cancelAcctEdit()
          })
          .catch(error => {
            this.cancelAcctEdit()
          })
    },
    printOrder(order) {
      this.$bus.$emit('startPrint', {
        type: 'order',
        contents: [order],
      })
    },
    reduceOrderItems(items) {
      let skus = {};
      items.forEach(item => {
        skus[item.product_sku] = (item.product_sku in skus) ? skus[item.product_sku] + 1 : 1;
      })

      let sku_list = '';

      for (let sku in skus) {
        if (sku_list.length > 0) {
          sku_list += ', ';
        }
        sku_list += sku + (skus[sku] > 1 ? ' (' + skus[sku] + ')' : '');
      }

      return sku_list
    },
    unprintOrder(order) {
      this.$store.dispatch(MARK_UNPRINTED_ORDERS, [order.id])
    },
    archiveOrder(order) {
      this.$store.dispatch(ARCHIVE_ORDER, [order.id])
    },
    unarchiveOrder(order) {
      this.$store.dispatch(UNARCHIVE_ORDER, [order.id])
    },
    voidOrder(order) {
      if (this.voidingOrder === null) {
        this.voidingOrder = order.id;
      }
      else {
        this.$store.dispatch(VOID_ORDER, [this.voidingOrder])
        this.voidingOrder        = null
        this.voidForm.voidReason = null
      }
    },
    unvoidOrder(order) {
      this.$store.dispatch(UNVOID_ORDER, [order.id])
    },
  },
};
</script>

<style lang="scss">

.table-hr {
  margin: 0;
  background-color: #aaa;
  height: 1px;
}

.void-order-reason-container {
  display: block;
  position: fixed;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.void-order-reason-dialog {
  display: block;
  position: absolute;
  background-color: #fff;
  padding: 1rem;
  border-radius: 1rem;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
}

.edit-account-number {
  &:hover {
    cursor: pointer;
  }
}
</style>



// WEBPACK FOOTER //
// OrderList.vue?089f1309